/** @format */

import type { Ga4EventKey } from 'src/analytics/ga4EventCatalog';

export interface Ga4BaseParams {
  /** Internal catalog key (distinct from GTM `event` / GA4 event name) */
  event_name?: Ga4EventKey | string;
  page_name?: string;
  page_category?: string;
  user_status?: 'logged_in' | 'logged_out';
  user_id?: string;
}

export interface Ga4WidgetClickParams {
  widget_name?: string;
  widget_position?: string;
  widget_category?: string;
}

export interface Ga4ComponentInteractionParams {
  component_name?: string;
  component_type?: string;
  component_position?: string;
}

export interface Ga4DocumentDownloadParams {
  document_name?: string;
  document_type?: string;
  document_category?: string;
}

export interface Ga4SearchNoResultsParams {
  search_term?: string;
  search_location?: string;
}

export interface Ga4SearchResultClickParams {
  search_term?: string;
  search_location?: string;
  content_id?: string;
  result_position?: number;
}

export interface Ga4SearchParams {
  search_term?: string;
  results_count?: number;
  search_location?: string;
}

export const GA4_BASE_PARAM_KEYS = Object.freeze([
  'event_name',
  'page_name',
  'page_category',
  'user_status',
  'user_id'
] as const);

export const GA4_EVENT_PARAM_KEYS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  widget_click: Object.freeze(['widget_name', 'widget_position', 'widget_category']),
  component_interaction: Object.freeze(['component_name', 'component_type', 'component_position']),
  document_download: Object.freeze(['document_name', 'document_type', 'document_category']),
  search: Object.freeze(['search_term', 'results_count', 'search_location']),
  search_no_results: Object.freeze(['search_term', 'search_location']),
  search_result_click: Object.freeze(['search_term', 'search_location', 'content_id', 'result_position'])
});

const INTEGER_PARAM_KEYS = new Set(['results_count', 'result_position']);

function isPresentValue(value: unknown): boolean {
  if (value === undefined || value === null) {
    return false;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return false;
  }
  return true;
}

function normalizeParamValue(key: string, value: unknown): string | number | undefined {
  if (INTEGER_PARAM_KEYS.has(key)) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return undefined;
    }
    return Math.trunc(numeric);
  }
  return value as string | number;
}

export function getAllowedGa4ParamKeys(eventKey: string): string[] {
  const eventKeys = GA4_EVENT_PARAM_KEYS[eventKey] || [];
  return [...GA4_BASE_PARAM_KEYS, ...eventKeys];
}

/**
 * Keeps only allowed GA4 parameter keys for the event; drops empty values.
 */
export function pickGa4Params(eventKey: string, rawParams: Record<string, unknown> = {}): Record<string, string | number> {
  const allowedKeys = new Set(getAllowedGa4ParamKeys(eventKey));
  const picked: Record<string, string | number> = {};

  Object.entries(rawParams).forEach(([key, value]) => {
    if (!allowedKeys.has(key) || !isPresentValue(value)) {
      return;
    }
    const normalized = normalizeParamValue(key, value);
    if (isPresentValue(normalized)) {
      picked[key] = normalized as string | number;
    }
  });

  return picked;
}
